import { relations, sql } from 'drizzle-orm';
import { index, integer, pgEnum, pgTable, primaryKey, serial, timestamp, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { typeIdDataType as typeId } from '../utils/typeid';
import type { Provider } from './account';
import { organization, subscription } from './organization';

export const ORGANIZATION_ACCOUNT_USAGE_TYPE = ['baseline', 'account_added', 'account_removed'] as const;
export type OrganizationAccountUsageType = (typeof ORGANIZATION_ACCOUNT_USAGE_TYPE)[number];

export const organizationAccountUsageTypeEnum = pgEnum('organization_account_usage_type', ORGANIZATION_ACCOUNT_USAGE_TYPE);

export const organizationAccountUsage = pgTable(
  'organization_account_usage',
  {
    id: serial().primaryKey(),
    organization_id: integer()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    application_public_id: typeId('app', 'application_public_id').notNull(),
    account_public_id: typeId('account', 'account_public_id').notNull(),
    provider: varchar({ length: 255 }).notNull().$type<Provider>(),
    recorded_at: timestamp({ withTimezone: true, mode: 'string' })
      .notNull()
      .default(sql`clock_timestamp()`),
    type: organizationAccountUsageTypeEnum().notNull(),
  },
  (t) => [
    index('organization_account_usage_organization_recorded_idx').on(t.organization_id, t.recorded_at, t.id),
    index('organization_account_usage_organization_application_recorded_idx').on(
      t.organization_id,
      t.application_public_id,
      t.recorded_at,
    ),
    index('organization_account_usage_account_recorded_idx').on(t.account_public_id, t.recorded_at),
    index('organization_account_usage_provider_recorded_idx').on(t.provider, t.recorded_at),
  ],
);

export const organizationAccountUsageSnapshot = pgTable(
  'organization_account_usage_snapshot',
  {
    id: serial().primaryKey(),
    subscription_id: integer()
      .notNull()
      .references(() => subscription.id, { onDelete: 'cascade' }),
    interval_start: timestamp({ withTimezone: true, mode: 'string' }).notNull(),
    interval_end: timestamp({ withTimezone: true, mode: 'string' }).notNull(),
    peak_accounts: integer().notNull(),
    peak_at: timestamp({ withTimezone: true, mode: 'string' }).notNull(),
    finalized_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    stripe_synced_at: timestamp({ withTimezone: true, mode: 'string' }),
  },
  (t) => [uniqueIndex('organization_account_usage_snapshot_subscription_interval_uq').on(t.subscription_id, t.interval_end)],
);

export const organizationAccountUsageSnapshotApplication = pgTable(
  'organization_account_usage_snapshot_application',
  {
    snapshot_id: integer()
      .notNull()
      .references(() => organizationAccountUsageSnapshot.id, { onDelete: 'cascade' }),
    application_public_id: typeId('app', 'application_public_id').notNull(),
    accounts_at_peak: integer().notNull(),
    closing_accounts: integer().notNull(),
  },
  (t) => [primaryKey({ columns: [t.snapshot_id, t.application_public_id] })],
);

export const organizationAccountUsageRelations = relations(organizationAccountUsage, ({ one }) => ({
  organization: one(organization, {
    fields: [organizationAccountUsage.organization_id],
    references: [organization.id],
  }),
}));

export const organizationAccountUsageSnapshotRelations = relations(organizationAccountUsageSnapshot, ({ one, many }) => ({
  subscription: one(subscription, {
    fields: [organizationAccountUsageSnapshot.subscription_id],
    references: [subscription.id],
  }),
  applications: many(organizationAccountUsageSnapshotApplication),
}));

export const organizationAccountUsageSnapshotApplicationRelations = relations(
  organizationAccountUsageSnapshotApplication,
  ({ one }) => ({
    snapshot: one(organizationAccountUsageSnapshot, {
      fields: [organizationAccountUsageSnapshotApplication.snapshot_id],
      references: [organizationAccountUsageSnapshot.id],
    }),
  }),
);
